import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'info' | 'success' | 'warning' | 'error';

const VARIANTS: Record<Variant, { wrap: string; icon: string; title: string }> = {
  info: {
    wrap: 'bg-secondary-100 border-secondary-200 text-secondary-900',
    icon: 'ri-information-line text-secondary-700',
    title: 'text-secondary-900',
  },
  success: {
    wrap: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    icon: 'ri-checkbox-circle-line text-emerald-600',
    title: 'text-emerald-800',
  },
  warning: {
    wrap: 'bg-amber-50 border-amber-200 text-amber-800',
    icon: 'ri-alert-line text-amber-600',
    title: 'text-amber-800',
  },
  error: {
    wrap: 'bg-rose-50 border-rose-200 text-rose-800',
    icon: 'ri-error-warning-line text-rose-600',
    title: 'text-rose-800',
  },
};

interface AlertProps {
  variant?: Variant;
  title?: string;
  children?: ReactNode;
  onClose?: () => void;
  className?: string;
}

export default function Alert({ variant = 'info', title, children, onClose, className }: AlertProps) {
  const styles = VARIANTS[variant];
  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-lg border px-4 py-3 text-sm', styles.wrap, className)}
    >
      <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
        <i className={cn(styles.icon, 'text-lg leading-none')} aria-hidden="true" />
      </span>
      <div className="flex-1 leading-relaxed">
        {title ? <p className={cn('font-semibold', styles.title)}>{title}</p> : null}
        {children ? <div className={cn(title && 'mt-0.5')}>{children}</div> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          className="w-6 h-6 flex items-center justify-center rounded-md text-current/70 hover:bg-black/5 cursor-pointer shrink-0"
        >
          <i className="ri-close-line text-lg" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}